import { getPlatformMark } from "@mairko/brand";
import type { Integration } from "./nav.js";
import { ArrowRightIcon, CheckIcon } from "lucide-react";

type Props = {
	item: Integration;
	actionLabel: string;
	onAction?: () => void;
};

function cardClass(status: Integration["status"]): string {
	switch (status) {
		case "soon":
			return "integration-card integration-card--soon";
		case "connected":
			return "integration-card integration-card--connected";
		case "attention":
			return "integration-card integration-card--attention";
		case "available":
			return "integration-card";
		default: {
			const _exhaustive: never = status;
			return _exhaustive;
		}
	}
}

function StatusBadge({ status }: { status: Integration["status"] }) {
	switch (status) {
		case "connected":
			return (
				<span className="integration-status integration-badge integration-badge--ok">
					<CheckIcon className="size-3" />
					Connected
				</span>
			);
		case "attention":
			return (
				<span className="integration-status integration-badge integration-badge--warn">
					Attention
				</span>
			);
		case "soon":
			return (
				<span className="integration-status integration-badge">Soon</span>
			);
		case "available":
			return (
				<span className="integration-status integration-badge">Available</span>
			);
		default: {
			const _exhaustive: never = status;
			return _exhaustive;
		}
	}
}

/** Catalog tile — oversized mark as atmosphere, copy in front. */
export function IntegrationCard({ item, actionLabel, onAction }: Props) {
	const mark = getPlatformMark(item.markId, 160);

	return (
		<button
			type="button"
			className={cardClass(item.status)}
			onClick={onAction}
			aria-label={`Open ${item.name}`}
		>
			<span className="integration-card-mark" aria-hidden>
				{mark ? (
					<span
						className="integration-mark"
						dangerouslySetInnerHTML={{ __html: mark.svg }}
					/>
				) : (
					<span className="integration-fallback">{item.name.slice(0, 1)}</span>
				)}
			</span>
			<span className="integration-card-body">
				<StatusBadge status={item.status} />
				<span className="integration-name">{item.name}</span>
				<span className="integration-blurb">{item.blurb}</span>
				<span className="integration-cta">
					{actionLabel}
					<ArrowRightIcon className="size-3.5" />
				</span>
			</span>
		</button>
	);
}
